import React from "react";
import { Link } from "react-router-dom";

const sections = [
    {
        title: "We Never See Your Bank Credentials",
        content: (
            <>
                <p>
                    When you connect an account, you log in through Plaid's secure portal, not through
                    OmniWorth. We never see, store, or have access to your bank usernames or passwords. Plaid
                    handles all authentication directly with your financial institution.
                </p>
                <p>
                    Plaid is used by over 12,000 apps including Venmo, Robinhood, and Coinbase. You can learn
                    more about how Plaid protects your data on their{" "}
                    <a href="https://plaid.com/security/" target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:underline">
                        security page
                    </a>.
                </p>
            </>
        ),
    },
    {
        title: "Read-Only Access",
        content: (
            <>
                <p>
                    OmniWorth can only view account balances, transactions, and holdings. Nobody (not
                    OmniWorth, not Plaid) can move money, initiate transfers, or make changes to any connected
                    account. This is enforced at the Plaid API level.
                </p>
            </>
        ),
    },
    {
        title: "Encryption",
        content: (
            <>
                <p>
                    All data is encrypted in transit using TLS/HTTPS and at rest using AES-256 encryption, the
                    same standard used by major banks. Passwords are hashed using bcrypt and never stored in
                    plain text. Plaid access tokens are encrypted before being stored in the database.
                </p>
            </>
        ),
    },
    {
        title: "Your Data, Your Control",
        content: (
            <>
                <p>
                    You can disconnect any account at any time from the Accounts page. You can delete your
                    entire account and all associated data from the Settings page. Upon deletion, all financial
                    data, account connections, and history are permanently removed within 30 days.
                </p>
            </>
        ),
    },
    {
        title: "Two-Factor Authentication",
        content: (
            <>
                <p>
                    OmniWorth supports optional two-factor authentication using authenticator apps (TOTP). You
                    also receive email alerts when a new device logs into your account. Account lockout after
                    repeated failed login attempts protects against brute force attacks.
                </p>
            </>
        ),
    },
    {
        title: "Infrastructure",
        content: (
            <>
                <p>
                    OmniWorth is hosted on Amazon Web Services (AWS) in the United States. Payment processing
                    is handled by Stripe, so OmniWorth never stores credit card numbers. Error monitoring uses
                    Sentry for quick issue detection without exposing user financial data.
                </p>
            </>
        ),
    },
];

export default function SecurityPage() {
    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
            <div className="text-center mb-12">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">Security</h1>
                <p className="text-gray-500 text-lg">How OmniWorth protects your financial data.</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 sm:p-12">
                <div className="space-y-10">
                    {sections.map((section, i) => (
                        <div key={i}>
                            <h2 className="text-xl font-semibold text-gray-900 mb-3">{section.title}</h2>
                            <div className="text-gray-700 leading-relaxed space-y-3">
                                {section.content}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 pt-8 border-t border-gray-200 text-center">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Still have questions?</h3>
                    <p className="text-gray-500 text-sm">
                        Visit our{" "}
                        <Link to="/contact" className="text-accent-600 hover:underline">contact page</Link>
                        {" "}or email us at{" "}
                        <a href="mailto:support@omniworth.net" className="text-accent-600 hover:underline">
                            support@omniworth.net
                        </a>.
                    </p>
                </div>
            </div>
        </div>
    );
}
